'use client';

import BrokerLogo from '@/components/UI/BrokerLogo';
import ChevDown from '@/components/UI/ChevDown';
import { toggleAllBrokers, toggleBroker } from '@/store/brokerMetricsHandlers';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { Loader2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

function MultiSelect({ placeHolder, variant = 'default' }: Readonly<{ placeHolder: string; variant?: 'default' | 'pill' }>) {
    const dispatch = useAppDispatch();
    const brokers = useAppSelector((state) => state.brokerMetrics.brokers);
    const comparedBrokers = useAppSelector((state) => state.brokerMetrics.comparedBrokers);
    const loadingBrokers = useAppSelector((state) => state.brokerMetrics.loadingBrokers);
    const [search, setSearch] = useState('');

    const filteredBrokers = useMemo(() => {
        const query = search.trim().toLowerCase();
        const matching = query ? brokers.filter((broker) => broker.display_name.toLowerCase().includes(query)) : brokers;
        return [...matching].sort((a, b) => {
            const aCompared = comparedBrokers.includes(a.code) ? 0 : 1;
            const bCompared = comparedBrokers.includes(b.code) ? 0 : 1;
            return aCompared - bCompared;
        });
    }, [brokers, search, comparedBrokers]);

    const filteredCodes = filteredBrokers.map((broker) => broker.code);
    const allFilteredCompared = filteredCodes.length > 0 && filteredCodes.every((code) => comparedBrokers.includes(code));

    const isPill = variant === 'pill';
    const triggerLabel = isPill
        ? comparedBrokers.length > 0
            ? `${comparedBrokers.length} Broker${comparedBrokers.length === 1 ? '' : 's'} Selected`
            : placeHolder
        : comparedBrokers.length > 0
          ? `${comparedBrokers.length} selected`
          : placeHolder;

    return (
        <div className={`flex flex-col gap-1.5 ${isPill ? 'w-full md:w-max' : 'w-max'}`}>
            {!isPill && (
                <label className='text-[11px] font-medium uppercase tracking-wide text-slate-500' htmlFor='broker-dropdown'>
                    Select Brokers to Compare{' '}
                </label>
            )}
            <div className={`dropdown dropdown-hover ${isPill ? 'w-full md:w-auto' : ''}`}>
                <div
                    tabIndex={0}
                    role='select'
                    className={`flex cursor-pointer items-center justify-between gap-2 border bg-white text-xs transition-colors hover:border-slate-400 ${
                        isPill
                            ? 'h-8 w-full rounded-[2px] border-[#E3E3E3] px-2 py-1.5 font-normal text-[#555] md:h-9 md:w-max md:rounded-lg md:border-slate-300 md:px-3 md:font-medium md:text-slate-700'
                            : 'h-9 w-[11rem] rounded-lg border-slate-300 px-3'
                    }`}>
                    <span className={isPill ? 'whitespace-nowrap' : 'text-slate-500'}>{triggerLabel}</span>
                    <ChevDown className={isPill ? 'size-5 md:size-4' : 'size-4'} />
                </div>
                <div
                    tabIndex={0}
                    className='menu dropdown-content z-30 mt-1 max-h-80 w-64 flex-nowrap overflow-y-auto rounded-lg border border-slate-200 bg-base-100 p-2 shadow-lg'>
                    <div className='relative mb-1 shrink-0'>
                        <Search size={13} className='absolute left-2 top-1/2 -translate-y-1/2 text-slate-400' />
                        <input
                            type='text'
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder='Search brokers'
                            className='w-full rounded-md border border-slate-200 bg-transparent py-1.5 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#0085FF]/40'
                        />
                    </div>
                    {filteredBrokers.length !== 0 && (
                        <label
                            key='all'
                            className='mb-1 flex w-full cursor-pointer items-center gap-2 rounded-md border-b border-slate-200 px-2 py-1.5 font-medium hover:bg-slate-100'>
                            <input
                                type='checkbox'
                                checked={allFilteredCompared}
                                onChange={() => dispatch(toggleAllBrokers(filteredCodes, !allFilteredCompared))}
                                className='checkbox checkbox-xs rounded checked:border-[#0782DA] [--chkbg:#0782DA] [--chkfg:#ffffff]'
                            />
                            <span className='text-xs'>Select All</span>
                        </label>
                    )}
                    {filteredBrokers.length === 0 && (
                        <div className='px-2 py-3 text-center text-xs text-slate-400'>No brokers match &quot;{search}&quot;</div>
                    )}
                    {filteredBrokers.map((broker) => {
                        const loading = loadingBrokers[broker.code];
                        return (
                            <label
                                key={broker.code}
                                className='flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-slate-100'>
                                <input
                                    type='checkbox'
                                    checked={comparedBrokers.includes(broker.code)}
                                    onChange={() => dispatch(toggleBroker(broker.code))}
                                    disabled={loading}
                                    className='checkbox checkbox-xs rounded checked:border-[#0782DA] [--chkbg:#0782DA] [--chkfg:#ffffff]'
                                />
                                <BrokerLogo broker={broker.display_name} logoUrl={broker.logo_url} size={16} />
                                <span className='flex-1 text-xs'>{broker.display_name}</span>
                                {loading && <Loader2 size={12} className='animate-spin text-slate-400' />}
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default MultiSelect;
